import { Grid, Stack } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';

import type { EnvironmentalDataFormData } from '../../../types/reports.js';
import { EMISSION_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { NO_REMOUNT, ValidatedNumberInput, ValidatedSelect } from '../../form/validatedFields.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
// Feldkataloge leben seit Plan 28-05 in kilnFields.ts (reines Modul, das auch
// previousValuesSource.ts nutzt) -- hier nur re-exportiert, damit alle bisherigen Importstellen
// (KilnAnnualValuesForm, KilnMeasurementCard, DrehofenSection) unveraendert bei `./KilnForm.js`
// bleiben.
import { MEASUREMENT_EMISSION_FIELDS } from './kilnFields.js';

export {
  KILN_ANNUAL_FIELDS,
  ALL_KILN_ANNUAL_FIELD_KEYS,
  MEASUREMENT_EMISSION_FIELDS,
  ALL_EMISSION_FIELD_KEYS,
  createEmptyMeasurement,
} from './kilnFields.js';

interface KilnFormProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  /** z. B. `rotaryKilns.0.measurements.2` -- alle Felder binden an `${fieldPrefix}.<feldname>`. */
  fieldPrefix: string;
  disabled?: boolean;
}

// Bezugssauerstoff braucht 2 statt 7 Nachkommastellen -- Feld-Override analog
// EMISSION_FACTOR_INPUT_PROPS (emissionFactor) in BrennstoffeSection.tsx.
const OXYGEN_REFERENCE_INPUT_PROPS = { ...EMISSION_INPUT_PROPS, decimalScale: 2 } as const;

// Liest einen verschachtelten Formularwert anhand eines Punktpfads -- dieselbe Logik wie das
// (nicht exportierte) getPath in validatedFields.tsx. Lokal dupliziert statt importiert, da
// validatedFields.tsx laut Plan-Constraint nicht geaendert werden darf und measurementDate bewusst
// keinen Validated*-Wrapper bekommt (kein Backend-Assert auf diesem Feld, Phase-17-05-Praezedenz
// fuer constraintfreie Felder).
function getNestedValue(values: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, values);
}

export function KilnForm({ form, fieldPrefix, disabled }: KilnFormProps) {
  // unitOnLabel (Vorgabe: nein) steuert, ob die Einheit als Label-Zusatz erscheint (ueber das
  // unit-Prop von ValidatedNumberInput) statt nur als Eingabe-Suffix. Nur "Organische
  // Verbindungen" setzt ihn: "Chlor- und Fluorverbindungen" und "Schwermetalle" fuehren je eine
  // Einheit fuer die ganze Gruppe und schreiben sie bereits in ihrer Abschnittsbeschreibung aus --
  // ein zusaetzlicher Klammerzusatz an 2 bzw. 16 Feldern waere Doppelung und wuerde die
  // dreispaltigen Labels unnoetig verlaengern. Nur die organischen Verbindungen haben
  // je Feld eine eigene Einheit, dort ist das Label die einzige Stelle, an der sie im leeren
  // Formular stehen kann.
  function renderEmissionField(fieldKey: string, label: string, unit?: string, unitOnLabel = false) {
    const unitSuffix = unit ? ` ${unit}` : undefined;

    // Seit dem Wegfall des Unsicherheits-Felds gibt es je Emission nur noch ein Wert-Feld -- der
    // Schadstofftyp steht daher direkt als Feld-Label (statt "Wert" mit separater Ueberschrift).
    return (
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={fieldKey}>
        <ValidatedNumberInput
          form={form}
          field={`${fieldPrefix}.${fieldKey}Value`}
          belowLimitField={`${fieldPrefix}.${fieldKey}BelowLimit`}
          kind="decimal"
          align="right"
          label={label}
          placeholder=""
          disabled={disabled}
          {...(unitOnLabel ? { unit } : { suffix: unitSuffix })}
          {...EMISSION_INPUT_PROPS}
        />
      </Grid.Col>
    );
  }

  const measurementDateField = `${fieldPrefix}.measurementDate`;
  const currentMeasurementDate = getNestedValue(form.getValues(), measurementDateField) as
    | string
    | null
    | undefined;

  return (
    <Stack gap="lg">
      <FormSubsection
        title="Messdaten"
        description='Das Zeichen links im Feld umschalten: „=“ für einen gemessenen Wert, „<“ für Werte unter der Bestimmungsgrenze — eingetragen wird dann die Grenze selbst.'
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            {/* DateInput arbeitet bereits mit ISO-Datumsstrings (YYYY-MM-DD) als Wert -- siehe
                @mantine/dates DateInput.d.ts: defaultValue?: DateValue | Date, onChange?:
                (value: DateStringValue | null) => void. Die alte new Date(...)/toISOString-
                Umrechnung aus KilnForm.tsx:140-148 entfaellt daher ersatzlos. Kein
                Validated*-Wrapper: das Feld traegt keinen Backend-Assert. */}
            <DateInput
              label="Messdatum"
              key={form.key(measurementDateField)}
              defaultValue={currentMeasurementDate ?? null}
              onChange={(value) => form.setFieldValue(measurementDateField, value, NO_REMOUNT)}
              disabled={disabled}
              valueFormat="DD.MM.YYYY"
              locale="de"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedSelect
              form={form}
              field={`${fieldPrefix}.valueBasis`}
              label="Werte bezogen auf"
              disabled={disabled}
              data={[
                { value: 'ten_percent', label: '10 Vol.-% O₂' },
                { value: 'actual', label: 'tatsächlicher O₂-Gehalt' },
              ]}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field={`${fieldPrefix}.oxygenReference`}
              kind="decimal"
              align="right"
              label="Bezugssauerstoff (O₂)"
              disabled={disabled}
              suffix=" %"
              max={21}
              {...OXYGEN_REFERENCE_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection
        title="Chlor- und Fluorverbindungen"
        description={`Alle Werte in ${MEASUREMENT_EMISSION_FIELDS.chlorFluor.unit}.`}
      >
        <Grid>
          {MEASUREMENT_EMISSION_FIELDS.chlorFluor.fields.map((f) =>
            renderEmissionField(f.key, f.label, MEASUREMENT_EMISSION_FIELDS.chlorFluor.unit),
          )}
        </Grid>
      </FormSubsection>

      <FormSubsection
        title="Schwermetalle"
        description={`Alle Werte in ${MEASUREMENT_EMISSION_FIELDS.heavyMetals.unit}.`}
      >
        <Grid>
          {MEASUREMENT_EMISSION_FIELDS.heavyMetals.fields.map((f) =>
            renderEmissionField(f.key, f.label, MEASUREMENT_EMISSION_FIELDS.heavyMetals.unit),
          )}
        </Grid>
      </FormSubsection>

      <FormSubsection
        title="Organische Verbindungen"
        description="Verschiedene Einheiten je nach Verbindung."
      >
        <Grid>
          {MEASUREMENT_EMISSION_FIELDS.organicCompounds.fields.map((f) =>
            renderEmissionField(f.key, f.label, f.unit, true),
          )}
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
